import { isEmpty } from 'lodash';
import {
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_A,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_B,
  getFundInvestmentNewMarketAdjustment,
} from 'pages/FundInvestments/common/constants';
import { parseValuationMarketAdjustment } from 'pages/FundInvestments/utils/parseValuationMarketAdjustment';
import { dbShortDate, getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { GetInitialMarketAdjustment } from './getInitialMarketAdjustment.types';

const getInitialMarketAdjustment = (params: GetInitialMarketAdjustment) => {
  const { currentMeasurementDate, fundInvestmentByAllocation, valuationByAllocation } = params;

  // Get market adjustments from valuation
  const { market_adjustments: valuationMarketAdjustments = [] } = getObjectValue(valuationByAllocation);

  // If valuation market adjustments is empty, create a new one
  if (isEmpty(valuationMarketAdjustments)) {
    // Use the first capital account date from fund investment details, or fall back to current measurement date
    const initialAsOfDate = getStringValue(
      dbShortDate(fundInvestmentByAllocation?.[0]?.capital_account_date ?? currentMeasurementDate)
    );

    // Create a new market adjustment object with initial as-of date and valuation id
    const newMarketAdjustment = getFundInvestmentNewMarketAdjustment({
      asOfDate: initialAsOfDate,
      valuationId: getNumberValue(valuationByAllocation?.id),
    });

    // Set default percentiles when creating new adjustment
    const percentileSelection = {
      percentile_selection_a: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_A,
      percentile_selection_b: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_B,
    };

    return {
      asOfDate: initialAsOfDate,
      marketAdjustment: newMarketAdjustment,
      marketComparisons: newMarketAdjustment.market_comparisons,
      percentileSelection,
    };
  }

  // Otherwise, use the first existing market adjustment from valuation
  const firstMarketAdjustment = valuationMarketAdjustments[0];
  const {
    as_of_date: asOfDate,
    market_comparisons: marketComparisons,
    percentile_selection_a: percentileSelectionA,
    percentile_selection_b: percentileSelectionB,
  } = getObjectValue(firstMarketAdjustment);

  const formattedAsOfDate = getStringValue(dbShortDate(asOfDate));
  const parsedMarketAdjustment = parseValuationMarketAdjustment(firstMarketAdjustment);

  // Extract or fallback percentiles from the adjustment
  const percentileSelection = {
    percentile_selection_a: getNumberValue(
      percentileSelectionA ?? FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_A
    ),
    percentile_selection_b: getNumberValue(
      percentileSelectionB ?? FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_B
    ),
  };

  return {
    asOfDate: formattedAsOfDate,
    marketAdjustment: parsedMarketAdjustment,
    marketComparisons,
    percentileSelection,
  };
};

export default getInitialMarketAdjustment;
